import React from 'react'
import { Theme } from 'features/ui/types'
import WidgetType from 'enums/WidgetType'
import DisplayOption from 'enums/DisplayOption'
import { DashboardWidgetType } from 'features/dashboard/types'
import { WordCloudWidgetsType } from 'components/widget/WordCloudWidgets/types'
import { CrosstabWidgetsType } from 'components/widget/CrosstabWidgets/types'
import { StyledContainer, StyledContent } from './index.css'
import Header from './Header'

interface WidgetContainerProps {
  widgetLayout: {
    i: string
    x: number
    y: number
    w: number
    h: number
  }
  widgetSetting: DashboardWidgetType
  displayTheme?: Theme
  children?: React.ReactNode
}

const WidgetContainer = ({
  widgetLayout,
  widgetSetting,
  displayTheme,
  children
}: WidgetContainerProps) => {
  const { type: widgetType } = widgetSetting
  const defaultPaddings = widgetLayout.h === 1 ? '0 24px 12px 24px' : '0 24px 24px 24px'
  const defaultContainerStyle = { padding: defaultPaddings }
  const defaultHeaderMargins = widgetLayout.h === 1 ? '0 0 0 0' : '0 0 10px 0'
  const defualtHeaderContainerStyle = { margin: defaultHeaderMargins }

  switch (widgetType) {
    case WidgetType.SOCIAL_WEB_CROSSTAB:
    case WidgetType.AUDIENCE_CROSSTAB:
      switch ((widgetSetting as CrosstabWidgetsType).displayOption) {
        case DisplayOption.TABLE:
          return (
            <StyledContainer displayTheme={displayTheme} style={defaultContainerStyle}>
              <Header
                widgetSetting={widgetSetting}
                headerContainerStyle={{ margin: '0 0 4px 0' }}
              />
              <StyledContent displayTheme={displayTheme} scrollableX={false} scrollableY={false}>
                {children}
              </StyledContent>
            </StyledContainer>
          )
        default:
          return (
            <StyledContainer displayTheme={displayTheme} style={defaultContainerStyle}>
              <Header
                widgetSetting={widgetSetting}
                headerContainerStyle={defualtHeaderContainerStyle}
              />
              <StyledContent displayTheme={displayTheme} scrollableX={false} scrollableY={false}>
                {children}
              </StyledContent>
            </StyledContainer>
          )
      }
    case WidgetType.SOCIAL_WEB_GEO_REGIONS:
    case WidgetType.AUDIENCE_GEO_REGIONS:
    case WidgetType.HTML:
      return (
        <StyledContainer displayTheme={displayTheme} style={defaultContainerStyle}>
          <Header
            widgetSetting={widgetSetting}
            headerContainerStyle={defualtHeaderContainerStyle}
          />
          <StyledContent displayTheme={displayTheme} scrollableX={false} scrollableY={false}>
            {children}
          </StyledContent>
        </StyledContainer>
      )
    case WidgetType.SOCIAL_WEB_TOPIC_COMPARISON:
    case WidgetType.AUDIENCE_TOPIC_COMPARISON:
    case WidgetType.SOCIAL_WEB_AUTHORS:
    case WidgetType.AUDIENCE_AUTHORS:
    case WidgetType.SOCIAL_WEB_DEMOGRAPHIC:
    case WidgetType.AUDIENCE_DEMOGRAPHIC:
    case WidgetType.SOCIAL_WEB_TRENDING:
    case WidgetType.AUDIENCE_TRENDING:
    case WidgetType.SOCIAL_WEB_SIDE_BY_SIDE:
    case WidgetType.SOCIAL_WEB_INDEX:
    case WidgetType.AUDIENCE_INDEX:
    case WidgetType.SOCIAL_WEB_CHANNELS:
    case WidgetType.SOCIAL_WEB_LANGUAGE:
    case WidgetType.AUDIENCE_LANGUAGE:
      return (
        <StyledContainer displayTheme={displayTheme} style={defaultContainerStyle}>
          <Header widgetSetting={widgetSetting} headerContainerStyle={{ margin: '0 0 4px 0' }} />
          <StyledContent displayTheme={displayTheme} scrollableX={false} scrollableY={false}>
            {children}
          </StyledContent>
        </StyledContainer>
      )
    case WidgetType.SOCIAL_WEB_POPULAR_LINKS:
    case WidgetType.AUDIENCE_POPULAR_LINKS:
    case WidgetType.SOCIAL_WEB_TAGS:
      return (
        <StyledContainer displayTheme={displayTheme} style={defaultContainerStyle}>
          <Header widgetSetting={widgetSetting} headerContainerStyle={{ margin: '0 0 4px 0' }} />
          <StyledContent displayTheme={displayTheme}>{children}</StyledContent>
        </StyledContainer>
      )
    // temporarily remove "wrapContent" since wordcloud will shrink when it's wrapped by a div
    // TODO: find A better fix for this issue to make style of widgets more consistent (CB-32895)
    case WidgetType.SOCIAL_WEB_SENTIMENT_DRIVERS:
    case WidgetType.AUDIENCE_SENTIMENT_DRIVERS:
    case WidgetType.SOCIAL_WEB_WORDCLOUD:
    case WidgetType.AUDIENCE_WORDCLOUD:
    case WidgetType.SOCIAL_WEB_PHOTO_CONCEPTS:
      switch ((widgetSetting as WordCloudWidgetsType).displayOption) {
        case DisplayOption.TABLE:
          return (
            <StyledContainer
              displayTheme={displayTheme}
              style={{ ...defaultContainerStyle, overflow: 'visible' }}>
              <Header
                widgetSetting={widgetSetting}
                headerContainerStyle={{ margin: '0 0 4px 0' }}
              />
              {children}
            </StyledContainer>
          )
        case DisplayOption.WORDCLOUD:
        default:
          return (
            <StyledContainer
              displayTheme={displayTheme}
              style={{ ...defaultContainerStyle, overflow: 'visible' }}>
              <Header
                widgetSetting={widgetSetting}
                headerContainerStyle={defualtHeaderContainerStyle}
              />
              {children}
            </StyledContainer>
          )
      }

    case WidgetType.HEADER:
      return (
        <StyledContainer
          style={{
            boxShadow: 'none',
            height: '92px',
            backgroundColor: 'inherit',
            padding: '24px 0',
            overflow: 'visible'
          }}>
          {children}
        </StyledContainer>
      )
    case WidgetType.IMAGE:
    case WidgetType.WHITESPACE:
      return (
        <StyledContainer
          displayTheme={displayTheme}
          style={{
            boxShadow: 'none',
            backgroundColor: 'inherit',
            padding: '0px'
          }}>
          {children}
        </StyledContainer>
      )
    case WidgetType.SOCIAL_WEB_POPULAR_POSTS:
      return (
        <StyledContainer displayTheme={displayTheme} style={defaultContainerStyle}>
          <Header
            widgetSetting={widgetSetting}
            headerContainerStyle={defualtHeaderContainerStyle}
          />
          <StyledContent displayTheme={displayTheme} scrollableX={false}>
            {children}
          </StyledContent>
        </StyledContainer>
      )
    // NI-2503: Sentiment and Source widgets render outer data labels
    // around their donut. The south-pole label of a dominant slice
    // always wants to live outside the donut's bounding box; with the
    // panel's default `overflow: hidden` that label gets clipped. Flip
    // these widget types to `overflow: visible` (the same trick the
    // Wordcloud / Sentiment Drivers branch above already uses) so the
    // label can render past the panel border into the dashboard's
    // inter-row gap instead of fighting an internal padding tradeoff
    // that shrinks the donut.
    //
    // SOCIAL_WEB_SOURCES is included here even though it also supports
    // an AREA `displayOption` (which is a line chart without outer
    // labels) — overflow:visible is a no-op for content that doesn't
    // try to escape, so it's safe to apply unconditionally and keeps
    // the switch case structure simple.
    case WidgetType.SOCIAL_WEB_SENTIMENT:
    case WidgetType.AUDIENCE_SENTIMENT:
    case WidgetType.SOCIAL_WEB_SOURCES:
      return (
        <StyledContainer
          displayTheme={displayTheme}
          style={{ ...defaultContainerStyle, overflow: 'visible' }}>
          <Header
            widgetSetting={widgetSetting}
            headerContainerStyle={defualtHeaderContainerStyle}
          />
          <StyledContent displayTheme={displayTheme} style={{ overflow: 'visible' }}>
            {children}
          </StyledContent>
        </StyledContainer>
      )
    case WidgetType.SOCIAL_WEB_DAY_AND_HOUR:
    case WidgetType.AUDIENCE_DAY_AND_HOUR:
    case WidgetType.SOCIAL_WEB_TIMELINE:
    case WidgetType.SOCIAL_WEB_TIMELINE_OLD:
    case WidgetType.SOCIAL_WEB_TIMELINE_COMPARISON:
    case WidgetType.AUDIENCE_TIMELINE:
    case WidgetType.AUDIENCE_TIMELINE_OLD:
    case WidgetType.AUDIENCE_TIMELINE_COMPARISON:
    case WidgetType.YOUTUBE_TIMELINE:
    case WidgetType.SOCIAL_WEB_DOMAINS:
    case WidgetType.RICH_TEXT:
    case WidgetType.SOCIAL_WEB_SUMMARY_METRICS_COMPACT:
    case WidgetType.AUDIENCE_SUMMARY_METRICS_COMPACT:
    default:
      return (
        <StyledContainer displayTheme={displayTheme} style={defaultContainerStyle}>
          <Header
            widgetSetting={widgetSetting}
            headerContainerStyle={defualtHeaderContainerStyle}
          />
          <StyledContent displayTheme={displayTheme}>{children}</StyledContent>
        </StyledContainer>
      )
  }
}

export default WidgetContainer
